<script lang="ts" module>
	export interface DescriptionRow {
		id: number;
		label: string;
		value: string;
		link?: string;
	}
</script>

<script lang="ts">
	import classNames from 'classnames';
	import type { EditionsPdpData } from '../utils/getPdpData/getPdpData';
	import { ArtistLinks } from '#lib/features/artists/components/ArtistLinks/index.js';
	import { getCartIconContext } from '#lib/features/checkout/utils/getCartIconContext/index.js';
	import { getCheckoutContext } from '#lib/features/checkout/utils/getCheckoutContext/index.js';
	import { trackArtworkView } from '#lib/features/checkout/utils/trackArtworkView/index.js';
	import { EditionsAddToCartButton } from '#lib/features/drop-page/components/EditionsAddToCartButton/index.js';
	import { getLiveDropInfoContext } from '#lib/features/live-drop-info/LiveDropInfoContext/utils/getLiveDropInfoContext/getLiveDropInfoContext.js';
	import { GlobalRoutes } from '#lib/features/navigation/constants/global-routes.js';
	import { PdpDetails } from '#lib/features/pdp/components/PdpDetails/index.js';
	import { getEditionJsonLd } from '#lib/features/seo/utils/getEditionJsonLd/getEditionJsonLd.js';
	import { EditionsMixpanelEvents } from '#lib/websites/editions/constants/mixpanel-events.js';
	import { EditionsRoutes } from '#lib/websites/editions/constants/routes.js';
	import { getEditionUrl } from '#lib/websites/editions/utils/getEditionUrl/getEditionUrl.js';
	import { RegisterInterestButtons } from '#lib/websites/register-interest/components/RegisterInterestButtons/index.js';
	import { page } from '$app/state';
	import { Accordion, AccordionItem } from '$global/components/Accordion';
	import { Markdown } from '$global/components/Markdown';
	import { Txt } from '$global/components/Txt';
	import { PdpDescription } from '$global/features/pdp/components/PdpDescription';
	import { JsonLd } from '$global/features/seo/components/JsonLd';
	import { buildContentKey } from '$global/utils/contentKey';
	import { formatPrice } from '$global/utils/formatPrice';

	let {
		dataCy,
		selectedItemIndex = $bindable(),
		isLightboxOpen = $bindable(),
		data: dataProp = undefined,
		embedded = false,
		onSizingGuide,
		...rest
	}: Props = $props();

	interface Props {
		dataCy: string;
		selectedItemIndex: number;
		isLightboxOpen: boolean;
		// The page's own data by default; the drop page dialog passes its own.
		data?: EditionsPdpData;
		// Inside the drop page dialog: no link back to the drop page.
		embedded?: boolean;
		// Runs once the sizing guide has selected the scale image.
		onSizingGuide?: () => void;
		class?: string;
	}

	let dataCyPrefix = $derived(`${dataCy}-description`);

	const { dropTypes } = getCartIconContext();
	const { liveDropInfoCtx } = getLiveDropInfoContext();
	const {
		cartItems: { cartItems },
	} = getCheckoutContext();

	let data = $derived(dataProp ?? (page.data as EditionsPdpData));
	let edition = $derived(data.edition);
	let detailsRows = $derived(data.detailsRows);
	let dropPageSlug = $derived(data.dropPageSlug);
	let dropConfigId = $derived(data.dropConfigId);
	let addToCartButtonLabel = $derived(data.addToCartButtonLabel);
	// REST returns integer primary keys; the content key, the cart item and the
	// view-tracking guard are all typed against GraphQL's `ID` strings.
	let editionId = $derived(String(edition.id));

	// Global content key (HB-959) — the shared join key across commerce and News.
	let contentKey = $derived(
		buildContentKey({
			artist: edition.artists
				?.map((artist) => artist?.Artists_id?.name)
				.filter(Boolean)
				.join(', '),
			idValue: editionId,
			idCollection: 'Editions',
			medium: edition.medium,
			// Band on what the buyer actually pays, matching the book PDP.
			price: edition.product?.sale_price ?? edition.product?.price,
			currency: edition.product?.currency,
			release: edition.product?.drop_config?.name ?? edition.series?.name,
			category: edition.series?.name,
			platform: 'editions',
		})
	);

	let cartItem = $derived({
		entityId: editionId,
		restricted: false,
		sku: edition.product?.sku,
		group: edition.series?.name,
		groupId: edition.series?.code,
		currency: edition.product?.currency,
		price: edition.product?.price,
		name: edition.name,
		dropConfigId: dropConfigId == null ? dropConfigId : String(dropConfigId),
		contentKey,
	});

	// Fires once per artwork; the id guard keeps re-renders (drop polling,
	// lightbox state) from re-firing for the same edition.
	let trackedEditionId: string | undefined = $state();

	$effect(() => {
		if (edition.id && trackedEditionId !== editionId) {
			trackedEditionId = editionId;
			trackArtworkView(EditionsMixpanelEvents.ArtworkView, contentKey);
		}
	});

	// Follows cart_end_date, not checkout_end_date: the drop page and checkout
	// close at the cart end, so the button must not outlive them.
	let showAddToCartButton = $derived(
		liveDropInfoCtx.liveDropInfo?.cartOpenEditionsCodes.includes(edition.code)
	);

	// Follows the client-side price, so ISR HTML never caches an Offer. Skipped
	// in the drop page dialog, whose head belongs to the drop page.
	let jsonLd = $derived(
		embedded
			? null
			: getEditionJsonLd(edition, {
					url: getEditionUrl(edition.code),
					isLive: !!showAddToCartButton,
				})
	);

	// Memoize the cart items keys to prevent infinite recalculation
	let cartItemKeys = $derived(Object.keys($cartItems));

	let editionsFromAnotherDropInTheCart = $derived(
		showAddToCartButton &&
			!!$dropTypes &&
			cartItemKeys.length > 0 &&
			cartItemKeys.some(
				(productId) => $dropTypes[productId]?.dropPageSlug !== dropPageSlug
			)
	);

	let showFrameWarning = $derived(edition?.series?.slug === 'civilisation');
</script>

{#if jsonLd}
	<JsonLd data={jsonLd} />
{/if}

<div data-cy={dataCyPrefix} class={rest.class}>
	<ArtistLinks
		artists={edition?.artists}
		txtVariant="h5"
		class="mt-6 mb-2 font-[500]"
		dataCy={`${dataCyPrefix}-artist-name`}
		artistDataCy={`${dataCyPrefix}-artist-name-link`}
	/>

	<Markdown
		component="h1"
		source={data.nameTokens}
		dataCy={`${dataCyPrefix}-edition-name`}
		class={classNames(
			'font-silka text-[1.75rem] leading-[2.25rem] font-[600] tracking-[-0.03125rem] text-gray-900 sm:text-[2.25rem] sm:leading-[2.5rem]',
			!(edition.product && showAddToCartButton) && !$dropTypes ? 'mb-6' : 'mb-4'
		)}
	/>

	{#if edition.product && showAddToCartButton}
		<Txt data-cy={`${dataCyPrefix}-price`} variant="h4" class="mb-2"
			>{formatPrice(edition.product?.price, edition.product?.currency)}</Txt
		>
		<Txt variant="body3" class="mb-4 text-gray-500">(plus applicable taxes)</Txt
		>
	{/if}

	{#if edition.product && showAddToCartButton}
		<div
			class={classNames('flex flex-col gap-4 sm:flex-row', {
				'mb-1': editionsFromAnotherDropInTheCart,
				'mb-4': showFrameWarning,
				'xs:mb-12 mb-6': !showFrameWarning && !editionsFromAnotherDropInTheCart,
			})}
		>
			<EditionsAddToCartButton
				dataCy={dataCyPrefix}
				class="xs:w-auto w-full"
				productId={edition.product?.id}
				disabled={editionsFromAnotherDropInTheCart}
				label={addToCartButtonLabel}
				{cartItem}
			/>
		</div>

		{#if editionsFromAnotherDropInTheCart}
			<Txt variant="body3" class="xs:mb-12 mb-6 text-gray-500"
				>You cannot add this edition to your cart as you have already added
				editions from another drop. Please <a
					href={`${EditionsRoutes.Checkout}/${$dropTypes?.[Object.keys($cartItems)[0]]?.dropPageSlug}`}
					class="underline">checkout</a
				> your current cart first or empty your cart.</Txt
			>
		{/if}

		{#if showFrameWarning}
			<Txt variant="body3" class="xs:mb-12 mb-6 text-gray-500"
				>You can select framing options during checkout.</Txt
			>
		{/if}
	{:else if edition?.show_register_interest && !!$dropTypes && !showAddToCartButton && (edition.code !== 'NNG1' || (edition.code === 'NNG1' && !liveDropInfoCtx.liveDropInfo?.liveEditionsCodes.includes('NNG2-5')))}
		<div class="my-5">
			<RegisterInterestButtons {dataCy} code={edition?.code} {contentKey} />
		</div>
	{/if}

	<Accordion multiple>
		<AccordionItem
			dataCy={`${dataCyPrefix}-details`}
			title="Details"
			class="border-t"
			defaultOpen
		>
			<PdpDetails
				bind:selectedItemIndex
				bind:isLightboxOpen
				{dataCy}
				rows={detailsRows}
				includeLightbox={!embedded}
				{onSizingGuide}
				href={dropPageSlug && !embedded
					? `${GlobalRoutes.EditionsHome}/${dropPageSlug}`
					: null}
				class={classNames('lg:pt-2', dropPageSlug ? 'pb-6' : 'pb-10')}
			/>
		</AccordionItem>
		<AccordionItem
			dataCy={`${dataCyPrefix}-description`}
			title="Description"
			defaultOpen
			class="border-t"
		>
			<PdpDescription
				{dataCy}
				class="lg:pt-2"
				markdown
				longDescription={edition.long_description}
				shortDescription={edition.short_description}
				longDescriptionTokens={data.longDescriptionTokens}
				shortDescriptionTokens={data.shortDescriptionTokens}
			/>
		</AccordionItem>
	</Accordion>
</div>
